// Core
import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import { DonutCard, FieldSearchSelect, LineChart, SectionCard } from "components/AtomsV2";

// Redux
import { useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { selectAllMeters, useGetMetersByCommunityIdQuery } from "state/meters/metersApiSlice";

// Utils
import { createChartXAxisLabel } from "utils/charts";
import { orderBy } from "utils/data";

function Charts({ dsoAggregated, dsoByMeter, granularity }) {
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["dso"]);

  const { meters } = useGetMetersByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data: metersData }) => ({
        meters: metersData && selectAllMeters(metersData),
      }),
    }
  );

  const cpeMeters = useMemo(() => (meters ?? []).filter((meter) => meter.type === "CPE"), [meters]);

  const [selectedMeterId, setSelectedMeterId] = useState(null);
  const activeMeterId = selectedMeterId ?? cpeMeters[0]?.id ?? null;
  const selectedMeter = cpeMeters.find((meter) => meter.id === activeMeterId);

  const meterIdToIdentifier = (meterId) =>
    meters?.find((meter) => meter.id === meterId)?.identifier;

  // Three-series legend shared by both bar charts.
  const legend = [
    { label: t("charts.series.grid_injection"), color: "var(--info)" },
    { label: t("charts.series.energy_surplus"), color: "var(--warning)" },
    { label: t("charts.series.collective_sc"), color: "var(--accent)" },
  ];

  const Legend = (
    <div className="flex items-center gap-[14px] flex-wrap text-[12px] text-ink-2">
      {legend.map((l) => (
        <span key={l.label} className="inline-flex items-center gap-[6px]">
          <i
            className="inline-block w-[9px] h-[9px] rounded-[3px]"
            style={{ background: l.color }}
          />
          {l.label}
        </span>
      ))}
    </div>
  );

  // Builds the 3-series energy profile for a list of {grid_injection,energy_surplus,grid_sc} rows.
  const buildSeries = (rows) => [
    {
      name: t("charts.series.grid_injection"),
      data: (rows ?? []).map((r) => r.grid_injection),
      color: "var(--info)",
    },
    {
      name: t("charts.series.energy_surplus"),
      data: (rows ?? []).map((r) => r.energy_surplus),
      color: "var(--warning)",
    },
    {
      // "Consumo pela Rede" = consumption from the grid (the large value shown in
      // the table/KPIs and already used by the per-meter donut), NOT grid_sc
      // (self-consumption, a tiny value) which made the bar invisible.
      name: t("charts.series.collective_sc"),
      data: (rows ?? []).map((r) => r.consumption),
      color: "var(--accent)",
    },
  ];

  const aggSeries = useMemo(() => buildSeries(dsoAggregated), [dsoAggregated, t]);
  const aggLabels = useMemo(
    () =>
      (dsoAggregated ?? []).map((r) => createChartXAxisLabel(r.datetime_timestamp, granularity)),
    [dsoAggregated, granularity]
  );

  const meterRows = activeMeterId ? dsoByMeter?.[activeMeterId] : null;
  const meterSeries = useMemo(() => buildSeries(meterRows), [meterRows, t]);
  const meterLabels = useMemo(
    () => (meterRows ?? []).map((r) => createChartXAxisLabel(r.datetime_timestamp, granularity)),
    [meterRows, granularity]
  );

  // Absolute per-meter totals (kWh), summed from the per-meter series — real
  // injection/consumption for every (known) meter, not normalized shares and
  // not capped to a top-N. Meters not resolvable to an identifier are skipped.
  const meterTotals = useMemo(() => {
    const sum = (rows, key) => (rows ?? []).reduce((s, r) => s + (Number(r[key]) || 0), 0);
    return Object.entries(dsoByMeter ?? {})
      .map(([meterId, rows]) => ({
        label: meterIdToIdentifier(meterId),
        injection: sum(rows, "grid_injection"),
        consumption: sum(rows, "consumption"),
      }))
      .filter((m) => m.label);
  }, [dsoByMeter, meters]);

  const injectionShare = useMemo(
    () =>
      orderBy([...meterTotals], "injection", "desc").map((m) => ({
        label: m.label,
        value: +m.injection.toFixed(2),
      })),
    [meterTotals]
  );

  const consumptionShare = useMemo(
    () =>
      orderBy([...meterTotals], "consumption", "desc").map((m) => ({
        label: m.label,
        value: +m.consumption.toFixed(2),
      })),
    [meterTotals]
  );

  return (
    <div className="flex flex-col gap-rec">
      <SectionCard
        icon="insights"
        title={t("charts.agg_evolution")}
        sub={t("charts.legend_help")}
        action={Legend}
      >
        <LineChart
          series={aggSeries}
          labels={aggLabels}
          height={330}
          unit=" kWh"
          type="bar"
          stacked
          xTitle={t("charts.axis_date", { ns: "common" })}
        />
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-rec">
        <DonutCard title={t("charts.by_meter_injection")} data={injectionShare} unit="kWh" />
        <DonutCard title={t("charts.by_meter_collective_sc")} data={consumptionShare} unit="kWh" />
      </div>

      <SectionCard
        icon="electric_meter"
        title={t("charts.meter_energy_profile_prefix")}
        sub={selectedMeter?.identifier}
        action={
          <FieldSearchSelect
            value={activeMeterId ?? ""}
            onChange={(v) => setSelectedMeterId(v)}
            options={cpeMeters.map((meter) => ({ value: meter.id, label: meter.identifier }))}
          />
        }
      >
        <div className="mb-2">{Legend}</div>
        <div className="text-[12px] text-ink-3 mb-2">{t("charts.legend_help")}</div>
        <LineChart
          series={meterSeries}
          labels={meterLabels}
          height={300}
          unit=" kWh"
          type="bar"
          stacked
          xTitle={t("charts.axis_date", { ns: "common" })}
        />
      </SectionCard>
    </div>
  );
}

Charts.defaultProps = {
  dsoAggregated: [],
  dsoByMeter: {},
  granularity: null,
};

Charts.propTypes = {
  dsoAggregated: PropTypes.arrayOf(
    PropTypes.shape({
      datetime_timestamp: PropTypes.string,
      grid_injection: PropTypes.number,
      energy_surplus: PropTypes.number,
      grid_sc: PropTypes.number,
    })
  ),
  dsoByMeter: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        datetime_timestamp: PropTypes.string,
        meter_id: PropTypes.string,
        grid_injection: PropTypes.number,
        energy_surplus: PropTypes.number,
        consumption: PropTypes.number,
        grid_sc: PropTypes.number,
      })
    )
  ),
  granularity: PropTypes.string,
};

export default Charts;
